import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "./api";
import {
  Pencil,
  Trash2,
  Save,
  X,
  Mail,
  Phone,
  Building2,
  User,
  Search,
} from "lucide-react";

function ContactsList() {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    company: "",
    phone: "",
    email: "",
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const res = await api.get("/api/contacts");
      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Error fetching contacts", err);
      alert("❌ Failed to load contacts");
      setContacts([]);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditForm({
      full_name: c.full_name || "",
      company: c.company || "",
      phone: c.phone || "",
      email: c.email || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({
      full_name: "",
      company: "",
      phone: "",
      email: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/api/contacts/${id}`, editForm);
      cancelEdit();
      fetchContacts();
    } catch (err) {
      console.error("❌ Error updating contact", err);
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to update contact";
      alert(`❌ ${msg}`);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact?")) return;

    try {
      await api.delete(`/api/contacts/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("❌ Error deleting contact", err);
      alert("❌ Failed to delete contact");
    }
  };

  // Filter contacts based on search
  const filteredContacts = contacts.filter((c) => {
    const search = searchTerm.toLowerCase();
    return (
      (c.full_name || "").toLowerCase().includes(search) ||
      (c.company || "").toLowerCase().includes(search) ||
      (c.email || "").toLowerCase().includes(search) ||
      (c.phone || "").toLowerCase().includes(search)
    );
  });

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Generate avatar color based on name
  const getAvatarColor = (name) => {
    const colors = [
      "#3b82f6",
      "#8b5cf6",
      "#ec4899",
      "#f59e0b",
      "#10b981",
      "#06b6d4",
      "#6366f1",
      "#ef4444",
    ];
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="main-content">
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6 }}>
            📇 Contacts
          </h1>
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            {filteredContacts.length} contact
            {filteredContacts.length !== 1 ? "s" : ""} found
          </p>
        </div>
        <Link
          to="/add-contact"
          className="add-button"
          style={{ padding: "10px 18px", fontSize: 14 }}
        >
          + Add Contact
        </Link>
      </div>

      {/* Search bar */}
      <div
        style={{
          position: "relative",
          marginBottom: 24,
          maxWidth: 500,
        }}
      >
        <Search
          size={18}
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.5,
          }}
        />
        <input
          type="text"
          placeholder="Search contacts by name, company, email, or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: "100%",
            padding: "12px 14px 12px 44px",
            borderRadius: 12,
            border: "1px solid rgba(148,163,184,0.25)",
            background: "rgba(255,255,255,0.04)",
            fontSize: 14,
            outline: "none",
          }}
        />
      </div>

      {/* Contacts grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
          gap: 16,
        }}
      >
        {filteredContacts.map((c) => {
          const isEditing = editingId === c.id;

          // ✅ company-first identity for avatar color + initials
          const identityText = c.company || c.full_name || "";
          const avatarColor = getAvatarColor(identityText);

          return (
            <div
              key={c.id}
              style={{
                borderRadius: 16,
                border: isEditing
                  ? "2px solid rgba(59,130,246,0.5)"
                  : "1px solid rgba(148,163,184,0.25)",
                background: isEditing
                  ? "rgba(59,130,246,0.05)"
                  : "rgba(255,255,255,0.04)",
                boxShadow: isEditing
                  ? "0 8px 32px rgba(59,130,246,0.15)"
                  : "0 4px 20px rgba(0,0,0,0.08)",
                overflow: "hidden",
                transition: "all 0.2s ease",
              }}
            >
              {/* Card header with avatar */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "18px",
                  background: isEditing
                    ? "rgba(59,130,246,0.08)"
                    : "rgba(255,255,255,0.02)",
                  borderBottom: "1px solid rgba(148,163,184,0.15)",
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    background: avatarColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: 18,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {getInitials(identityText)}
                </div>

                {/* Company (top) and Name (secondary) */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {isEditing ? (
                    <input
                      name="full_name"
                      value={editForm.full_name}
                      onChange={handleEditChange}
                      placeholder="Full Name"
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid rgba(59,130,246,0.5)",
                        background: "rgba(255,255,255,0.1)",
                        fontSize: 16,
                        fontWeight: 600,
                        outline: "none",
                      }}
                    />
                  ) : (
                    <>
                      {/* ✅ Company on top */}
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          marginBottom: 4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.company || "Individual / No company"}
                      </div>

                      {/* ✅ Person name under company */}
                      <div
                        style={{
                          fontSize: 13,
                          opacity: 0.75,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <User size={14} />
                        {c.full_name || "No contact name"}
                      </div>
                    </>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", gap: 6 }}>
                  {isEditing ? (
                    <>
                      <button
                        className="icon-btn"
                        onClick={() => saveEdit(c.id)}
                        title="Save"
                        type="button"
                        style={{ background: "#10b981", color: "white" }}
                      >
                        <Save size={16} />
                      </button>
                      <button
                        className="icon-btn ghost"
                        onClick={cancelEdit}
                        title="Cancel"
                        type="button"
                      >
                        <X size={16} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="icon-btn ghost"
                        onClick={() => startEdit(c)}
                        title="Edit"
                        type="button"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        className="icon-btn danger"
                        onClick={() => deleteContact(c.id)}
                        title="Delete"
                        type="button"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Card body with contact details */}
              <div
                style={{
                  padding: "18px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                {/* Company */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "rgba(139,92,246,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#8b5cf6",
                      flexShrink: 0,
                    }}
                  >
                    <Building2 size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
                      Company
                    </div>
                    {isEditing ? (
                      <input
                        name="company"
                        value={editForm.company}
                        onChange={handleEditChange}
                        placeholder="Company name"
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          borderRadius: 6,
                          border: "1px solid rgba(148,163,184,0.3)",
                          background: "rgba(255,255,255,0.08)",
                          fontSize: 14,
                          outline: "none",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.company || "—"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "rgba(34,197,94,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#10b981",
                      flexShrink: 0,
                    }}
                  >
                    <Phone size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
                      Phone
                    </div>
                    {isEditing ? (
                      <input
                        name="phone"
                        value={editForm.phone}
                        onChange={handleEditChange}
                        placeholder="Phone number"
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          borderRadius: 6,
                          border: "1px solid rgba(148,163,184,0.3)",
                          background: "rgba(255,255,255,0.08)",
                          fontSize: 14,
                          outline: "none",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.phone || "—"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "rgba(59,130,246,0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#3b82f6",
                      flexShrink: 0,
                    }}
                  >
                    <Mail size={18} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 2 }}>
                      Email
                    </div>
                    {isEditing ? (
                      <input
                        name="email"
                        value={editForm.email}
                        onChange={handleEditChange}
                        placeholder="Email address"
                        type="email"
                        style={{
                          width: "100%",
                          padding: "6px 10px",
                          borderRadius: 6,
                          border: "1px solid rgba(148,163,184,0.3)",
                          background: "rgba(255,255,255,0.08)",
                          fontSize: 14,
                          outline: "none",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.email || "—"}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {filteredContacts.length === 0 && (
        <div
          style={{
            padding: 60,
            textAlign: "center",
            opacity: 0.6,
          }}
        >
          {searchTerm ? (
            <>
              <div style={{ fontSize: 16, marginBottom: 8 }}>
                No contacts found matching "{searchTerm}"
              </div>
              <div style={{ fontSize: 14 }}>Try adjusting your search terms</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 16, marginBottom: 8 }}>No contacts yet</div>
              <div style={{ fontSize: 14 }}>
                Click "Add Contact" to create your first contact
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ContactsList;
